import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * Design Preview – Retning A: Editorial Noir + Magenta
 * Standalone page (no auth, no header) for å vise tone, typografi og komponenter.
 */

const PALETTE = [
  { name: "Bakgrunn",       hex: "#0A0A0A", note: "Pure noir – hovedflate" },
  { name: "Overflate",      hex: "#141414", note: "Kort, paneler, modal" },
  { name: "Krittkrem",      hex: "#F2EBE0", note: "Hovedtekst – varm, ikke kald hvit" },
  { name: "Demp",           hex: "#8B8378", note: "Sekundærtekst, metadata" },
  { name: "Magenta (signatur)", hex: "#E91E63", note: "Logo + sjeldne aksenter (knapper, hover)" },
  { name: "Forbrent kobber", hex: "#B26A3F", note: "Alternativ aksent for kategorier" },
  { name: "Dyp oxblood",    hex: "#5C1A24", note: "Reservert for advarsler / 18+" },
];

const PROFILES = [
  { username: "Lina",         city: "Oslo",       online: true,  age: 28 },
  { username: "Henrik",       city: "Bergen",     online: true,  age: 34 },
  { username: "AnonymOslo",   city: "Oslo",       online: false, age: 41 },
  { username: "NattSjel",     city: "Stavanger",  online: true,  age: 26 },
  { username: "Mira",         city: "Trondheim",  online: false, age: 31 },
  { username: "Vågar",        city: "Tromsø",     online: true,  age: 29 },
  { username: "ParBergen",    city: "Bergen",     online: false, age: 37 },
  { username: "RedaktørR",    city: "Oslo",       online: true,  age: 45 },
];

const Swatch = ({ name, hex, note }) => (
  <div className="flex items-center gap-4 py-3 border-b border-white/5">
    <div className="h-12 w-12 rounded-sm shrink-0 ring-1 ring-white/10" style={{ background: hex }} />
    <div className="flex-1 min-w-0">
      <div className="font-fraunces text-[#F2EBE0] text-base leading-tight">{name}</div>
      <div className="font-manrope text-[11px] uppercase tracking-[0.2em] text-[#8B8378] mt-0.5">{hex}</div>
    </div>
    <div className="hidden sm:block text-xs text-[#8B8378] italic max-w-[200px] text-right">{note}</div>
  </div>
);

const ProfileTile = ({ p }) => (
  <div className="group relative aspect-[3/4] overflow-hidden bg-[#141414] ring-1 ring-white/5 hover:ring-[#E91E63]/40 transition-all duration-300 cursor-pointer">
    {/* Placeholder image area – linear gradient for now */}
    <div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(135deg,
          hsl(${(p.username.charCodeAt(0) * 7) % 360} 30% 22%),
          hsl(${(p.username.charCodeAt(0) * 11) % 360} 25% 12%))`,
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent" />

    {/* Online dot */}
    <div className="absolute top-3 right-3">
      <span className={`block h-2 w-2 rounded-full ring-2 ring-black/60 ${p.online ? "bg-[#E91E63]" : "bg-white/30"}`} />
    </div>

    {/* Footer text */}
    <div className="absolute left-0 right-0 bottom-0 p-4">
      <div className="font-manrope text-[10px] uppercase tracking-[0.3em] text-[#8B8378] mb-1">
        {p.city} · {p.age}
      </div>
      <div className="font-fraunces italic text-[#F2EBE0] text-2xl leading-none transition-transform duration-300 group-hover:translate-x-1">
        {p.username}
      </div>
    </div>

    {/* Subtle hover line */}
    <div className="absolute left-4 right-4 bottom-3 h-px bg-[#E91E63] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
  </div>
);

export default function DesignPreview() {
  const [filter, setFilter] = useState("Alle");
  const filters = ["Alle", "Nær deg", "Nye", "Online"];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2EBE0]" style={{
      backgroundImage: "radial-gradient(800px 400px at 85% -10%, rgba(233,30,99,0.06), transparent 60%), radial-gradient(900px 500px at -5% 110%, rgba(178,106,63,0.04), transparent 60%)"
    }}>
      <style>{`
        .font-fraunces { font-family: 'Fraunces', 'Cormorant Garamond', serif; }
        .font-italiana { font-family: 'Italiana', 'Fraunces', serif; }
        .font-script   { font-family: 'Allura', cursive; }
        .font-manrope  { font-family: 'Manrope', system-ui, sans-serif; }
        .preview-rule { border-top: 1px solid rgba(242,235,224,0.08); }
      `}</style>

      {/* Top label */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378]">
            Design forhåndsvisning · Retning A · Editorial Noir
          </div>
          <Link to="/" className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378] hover:text-[#F2EBE0] transition-colors">
            ← Tilbake til siden
          </Link>
        </div>
      </div>

      {/* HERO – brand name */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20 text-center">
        <div className="font-manrope text-[10px] uppercase tracking-[0.5em] text-[#8B8378] mb-10">
          Vol. I · Ekte norske amatører · 2026
        </div>
        <h1 className="font-script text-8xl sm:text-9xl lg:text-[180px] leading-[0.9]"
            style={{ color: "#F71868", textShadow: "0 0 60px rgba(247,24,104,0.22)" }}>
          Norsk Erotikk
        </h1>
        <div className="mt-6 font-fraunces italic text-2xl sm:text-3xl text-[#F2EBE0]/80">
          et voksent fellesskap, kuratert med smak.
        </div>
        <div className="flex items-center justify-center gap-3 mt-10">
          <button className="px-7 py-3 bg-[#E91E63] text-black font-manrope text-sm uppercase tracking-[0.2em] hover:bg-[#F2EBE0] transition-colors duration-300">
            Bli medlem
          </button>
          <button className="px-7 py-3 bg-transparent text-[#F2EBE0] border border-[#F2EBE0]/20 font-manrope text-sm uppercase tracking-[0.2em] hover:border-[#E91E63] hover:text-[#E91E63] transition-colors duration-300">
            Logg inn
          </button>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6">
        <hr className="preview-rule" />
      </div>

      {/* Typography */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378] mb-3">01 — Typografi</div>
        <h2 className="font-fraunces text-5xl sm:text-6xl text-[#F2EBE0] mb-12 italic">Et alfabet med karakter.</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          <div>
            <div className="font-italiana text-7xl text-[#E91E63] leading-none">Italiana</div>
            <div className="text-[#8B8378] text-xs uppercase tracking-[0.3em] mt-3 font-manrope">
              Logo · "Norsk Erotikk"
            </div>
            <p className="text-[#F2EBE0]/70 text-sm leading-relaxed mt-3 font-manrope">
              Tynn, klassisk, sjeldent brukt på erotiske sider – derfor distinkt. Kun i logo og på særlig store øyeblikk.
            </p>
          </div>

          <div>
            <div className="font-fraunces italic text-6xl text-[#F2EBE0] leading-none">Fraunces</div>
            <div className="text-[#8B8378] text-xs uppercase tracking-[0.3em] mt-3 font-manrope">
              Display · seksjonsoverskrifter
            </div>
            <p className="text-[#F2EBE0]/70 text-sm leading-relaxed mt-3 font-manrope">
              Variable serif med "wonky" optisk justering. Bruk italic for stemning. Helt motpolen til Inter-massen.
            </p>
          </div>

          <div>
            <div className="font-manrope text-5xl text-[#F2EBE0] leading-tight font-medium">
              Manrope står stødig.
            </div>
            <div className="text-[#8B8378] text-xs uppercase tracking-[0.3em] mt-3 font-manrope">
              Body · all UI-tekst
            </div>
            <p className="text-[#F2EBE0]/70 text-sm leading-relaxed mt-3 font-manrope">
              Geometrisk sans-serif, høy lesbarhet på lange forumtråder. Profesjonell og moderne uten å skrike.
            </p>
          </div>

          <div>
            <div className="font-manrope text-[11px] uppercase tracking-[0.4em] text-[#8B8378]">
              Kategori · Erotikk · Stavanger · Norge · 2026
            </div>
            <div className="text-[#8B8378] text-xs uppercase tracking-[0.3em] mt-4 font-manrope">
              Mikro · metadata
            </div>
            <p className="text-[#F2EBE0]/70 text-sm leading-relaxed mt-3 font-manrope">
              Liten Manrope med stor letter-spacing. Brukes for å «sette tone» – som etiketter i et magasin.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6"><hr className="preview-rule" /></div>

      {/* Palette */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378] mb-3">02 — Palett</div>
        <h2 className="font-fraunces italic text-5xl sm:text-6xl text-[#F2EBE0] mb-4">
          Sort, krittkrem, et streif av magenta.
        </h2>
        <p className="text-[#F2EBE0]/60 max-w-2xl mb-10 font-manrope text-sm leading-relaxed">
          Magenta er signaturen, brukt med tilbakeholdenhet. Krittkremen erstatter den klinisk-hvite og gir en varmere, mer redaksjonell følelse. Lilla har vi gått helt vekk fra (det var det NakenTid og NakenPrat hadde).
        </p>

        <div className="border-t border-white/5">
          {PALETTE.map((p) => <Swatch key={p.hex} {...p} />)}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6"><hr className="preview-rule" /></div>

      {/* Profile grid */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378] mb-3">03 — Bilde- og profil-grid</div>
            <h2 className="font-fraunces italic text-5xl text-[#F2EBE0]">Kuratert, ikke kaos.</h2>
          </div>
          {/* Filter pills */}
          <div className="flex gap-2">
            {filters.map((f) => (
              <button key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-manrope transition-colors ${
                  filter === f
                    ? "bg-[#F2EBE0] text-black"
                    : "bg-transparent text-[#F2EBE0]/60 border border-white/10 hover:border-[#E91E63] hover:text-[#E91E63]"
                }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {PROFILES.map((p) => <ProfileTile key={p.username} p={p} />)}
        </div>

        <p className="text-[#8B8378] text-xs italic mt-6 font-fraunces">
          Brukernavn i kursiv-serif, by/alder i mikrotypografi. Ingen lilla flater. Online-prikk er signatur-magenta.
        </p>
      </section>

      <div className="max-w-6xl mx-auto px-6"><hr className="preview-rule" /></div>

      {/* Editorial article-style card example */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378] mb-3">04 — Forum-tråd</div>
        <h2 className="font-fraunces italic text-5xl text-[#F2EBE0] mb-10">
          Tråden som et essay.
        </h2>

        <article className="border-t border-white/10 pt-10 max-w-3xl">
          <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#E91E63] mb-3">
            Diskusjon · Samliv og forhold
          </div>
          <h3 className="font-fraunces text-4xl sm:text-5xl text-[#F2EBE0] leading-tight mb-4">
            Erfaringer med åpne forhold?
          </h3>
          <div className="font-manrope text-xs uppercase tracking-[0.3em] text-[#8B8378] mb-8">
            Av <span className="text-[#F2EBE0]">Mira</span> · 31 år · Trondheim · 12 timer siden · 24 svar
          </div>
          <p className="font-fraunces text-lg text-[#F2EBE0]/85 leading-relaxed mb-4">
            Vurderer å åpne opp forholdet sammen med min partner. Vi er nysgjerrige på hvordan andre har funnet en god balanse.
          </p>
          <p className="font-fraunces text-lg text-[#F2EBE0]/85 leading-relaxed">
            Hvordan satte dere grenser? Hvordan håndterte dere sjalusi? Og – kanskje viktigst – snakker dere fortsatt sammen?
          </p>

          <div className="mt-10 flex items-center gap-3">
            <button className="px-5 py-2.5 bg-[#E91E63] text-black font-manrope text-xs uppercase tracking-[0.25em] hover:bg-[#F2EBE0] transition-colors">
              Svar i tråden
            </button>
            <button className="px-5 py-2.5 bg-transparent text-[#F2EBE0]/70 border-b border-[#F2EBE0]/30 font-manrope text-xs uppercase tracking-[0.25em] hover:text-[#E91E63] hover:border-[#E91E63] transition-colors">
              Følg tråden
            </button>
            <span className="ml-auto font-manrope text-[10px] uppercase tracking-[0.3em] text-[#8B8378] italic">
              Rapporter
            </span>
          </div>
        </article>
      </section>

      <div className="max-w-6xl mx-auto px-6"><hr className="preview-rule" /></div>

      {/* Footer note */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="font-manrope text-[10px] uppercase tracking-[0.4em] text-[#8B8378] mb-3">Stemning</div>
        <h2 className="font-fraunces italic text-4xl sm:text-5xl text-[#F2EBE0] leading-tight mb-6 max-w-3xl">
          Estetisk. Brukervennlig. Litt eksklusiv.
        </h2>
        <p className="font-manrope text-[#F2EBE0]/60 max-w-2xl text-sm leading-relaxed">
          Helhetsfølelsen skal ligne et samtidsmagasin med voksent innhold – ikke en spammy datingside. 
          Mye luft, lite klutter, sterke typografiske kontraster og bilder som får plass til å puste.
          Knappene er flate og rektangulære, kantlinjene er hårtynne, og fargene brukes som krydder, ikke som dekorasjon.
        </p>

        <div className="mt-12 font-italiana text-3xl text-[#E91E63]/90">
          Norsk Erotikk
        </div>
        <div className="font-fraunces italic text-[#8B8378] text-sm">
          Ekte norske amatører · 18+
        </div>
      </section>

      <div className="h-16" />
    </div>
  );
}
